import Store from '../../index';
import * as assetsHelpers from '../../../helpers/assets';
import localization from '../../../shared/strings';
import * as utils from '../../../shared/utils';
import Toast from '../../../components/Toast';
import TYPES from '../../action-types';
import {
  updateCount as updateCountAction,
} from '../../keywords/actions';
import Logger from '../../../services/Logger';
import asyncCheckMultipleChanges from './asyncCheckMultipleChanges';
import { reloadCurrentPage } from '../../../helpers/history';
import { showWarningDetachingSeveralKeywords } from '../../helpers/assets';
import { keywordsRemovedFromSelection } from '../../helpers/keywordsRemovedByCascade';

/**
 * Remove keyword from assets
 * @param {string} keywordName
 * @param {string[]?} ids - assets IDs
 */
const detachKeyword = (keywordID, ids) => async (dispatch, getAll) => {
  const { items, selectedAssetsAggregatedData } = getAll().assets;
  const selectedItems = ids || getAll().assets.selectedItems;
  const keywordsToRemove = keywordsRemovedFromSelection([keywordID], {
    selectedIds: selectedItems,
    items,
    aggregatedKeywords: selectedAssetsAggregatedData?.keywords,
  });
  /**
   * One child stays at 1: cascade removes descendants, not that child's parents.
   * Count can be higher than the server result when selected assets are not loaded;
   * see keywordsRemovedFromSelection. It never changes what is sent to the API.
   */
  if (keywordsToRemove.length > 1) {
    const confirmed = await showWarningDetachingSeveralKeywords(keywordsToRemove.length, selectedItems.length);
    if (!confirmed) return false;
  }

  try {
    await asyncCheckMultipleChanges(selectedItems.length);
  } catch (error) {
    /** User cancelled operation */
    return false;
  }

  try {
    dispatch({ type: TYPES.ASSETS.DETACH_KEYWORD.START });

    /** wait for response */
    const { data } = await assetsHelpers.detachKeyword(selectedItems, keywordID);
    if (data.hasNotPermittedAssets) {
      Toast(localization.NO_COLLECTION_PERMISSION_TO_ACCESS('detach keywords'), { autoClose: false });
    }

    const detachedKeywordIds = Array.isArray(data.detachedKeywordIds)
      ? data.detachedKeywordIds.map((id) => String(id))
      : [keywordID];
    const detachments = Array.isArray(data.detachments)
      ? data.detachments.map((detachment) => ({
        assetId: String(detachment.assetId),
        keywordIds: detachment.keywordIds.map((id) => String(id)),
      }))
      : null;

    dispatch({
      type: TYPES.ASSETS.DETACH_KEYWORD.COMPLETE,
      payload: {
        ids,
        keywordsIds: detachedKeywordIds,
        detachments,
        userId: Store.getState().user._id,
      },
    });
    if (detachments) {
      const removedCounts = detachments.reduce((counts, { keywordIds: removedIds }) => {
        removedIds.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
        return counts;
      }, new Map());
      removedCounts.forEach((count, id) => {
        dispatch(updateCountAction({ _id: id, value: -count }));
      });
    } else {
      detachedKeywordIds.forEach((id) => {
        dispatch(updateCountAction({ _id: id, value: -selectedItems.length }));
      });
    }
    return true;
  } catch (error) {
    Logger.error(new Error('Can not attach keyword to asset'), { error }, [
      'DetachKeywordFailed',
      { errorMessage: (error && error.message) || 'NoMessage' },
    ]);
    const errorStatus = utils.getStatusFromResponceError(error);
    const errorMessage = errorStatus === 403
      ? localization.NO_PERMISSION_TO_ACCESS
      : utils.getDataFromResponceError(error, 'msg');
    if (errorMessage) {
      Toast(errorMessage, { autoClose: false, type: 'error' });
    } else {
      Toast(localization.DETAILS.textCantUpdateKeywords, {
        autoClose: false,
        type: 'error',
        onOk: reloadCurrentPage,
        btnOkValue: localization.HISTORY.textBtnRefresh,
      });
    }
    dispatch({ type: TYPES.ASSETS.DETACH_KEYWORD.FAILED, error });
    return false;
  }
};

export default detachKeyword;
